import { ProductWithSales } from "./calculations";

/**
 * Types de recommandations possibles pour les produits
 */
export enum ProductRecommendation {
  PERFORMANT = "Performant",
  AUGMENTER_PRIX = "Augmenter prix",
  A_POUSSER = "À pousser",
  A_ETUDIER = "À étudier",
  RENEGOCIER = "Renégocier",
  A_LIQUIDER = "À liquider",
  A_RETIRER = "À retirer",
}

/**
 * Niveaux de priorité pour les produits (1 = priorité maximale, 6 = priorité minimale)
 */
export enum PriorityLevel {
  PRIO_1 = 1,
  PRIO_2 = 2,
  PRIO_3 = 3,
  PRIO_4 = 4,
  PRIO_5 = 5,
  PRIO_6 = 6,
}

/**
 * Interface pour les résultats du scoring des produits
 */
export interface ProductScoringResult {
  recommendation: ProductRecommendation;
  priorityLevel: PriorityLevel; // Niveau de priorité (1-6)
  bgColor: string; // Couleur de fond pour l'affichage
  rowClass: string; // Classe CSS pour la ligne du tableau
  marginScore: number; // Score normalisé pour la marge (0-100)
  priceScore: number; // Score normalisé pour le prix (0-100)
  quantityScore: number; // Score normalisé pour la quantité vendue (0-100)
  totalScore: number; // Score global combiné
  marginLevel: "high" | "medium" | "low"; // Niveau de marge (élevé, moyen, faible)
  priceLevel: "high" | "medium" | "low"; // Niveau de prix (élevé, moyen, faible)
  quantityLevel: "high" | "medium" | "low"; // Niveau de ventes (élevé, moyen, faible)
}

/**
 * Interface pour les résultats du scoring de réapprovisionnement
 */
export interface ReorderScoringResult {
  priorityScore: number; // Score de priorité pour le réapprovisionnement (0-100)
  recommendedQuantity: number; // Quantité recommandée pour la commande
  daysUntilStockout: number; // Nombre de jours avant rupture de stock
  priorityLevel: "CRITIQUE" | "HAUTE" | "MOYENNE" | "BASSE"; // Niveau de priorité
  priorityColor: string; // Couleur pour l'affichage de la priorité
}

/**
 * Normalise une valeur entre 0 et 100 en fonction des valeurs min et max
 * @param value Valeur à normaliser
 * @param min Valeur minimale
 * @param max Valeur maximale
 * @returns Valeur normalisée entre 0 et 100
 */
export const normalizeValue = (
  value: number,
  min: number,
  max: number
): number => {
  if (max === min) return 50; // Éviter division par zéro
  return ((value - min) / (max - min)) * 100;
};

/**
 * Calcule les seuils relatifs en fonction des valeurs du catalogue
 * Ces seuils sont calculés dynamiquement en fonction de la distribution des valeurs
 */
interface ScoringThresholds {
  marginLow: number;
  marginHigh: number;
  priceLow: number;
  priceHigh: number;
  quantityLow: number;
  quantityHigh: number;
}

/**
 * Calcule les seuils pour le scoring des produits
 * Utilise une approche basée sur les percentiles pour déterminer les seuils
 * @param products Liste des produits avec leurs données de vente
 * @returns Seuils pour le scoring
 */
const calculateThresholds = (
  products: ProductWithSales[]
): ScoringThresholds => {
  // Filtrer les produits sans ventes pour ne pas fausser les calculs
  // Exclure également les produits qui ont 0 en stock et 0 en ventes
  const productsWithSales = products.filter(
    (p) => !(p.totalInventory === 0 && p.quantitySold === 0)
  );

  if (productsWithSales.length === 0) {
    // Valeurs par défaut si aucun produit n'a de ventes
    return {
      marginLow: 55, // Seuil de marge faible (< 55%)
      marginHigh: 65, // Seuil de marge élevée (> 65%)
      priceLow: 25, // 25e percentile pour le prix
      priceHigh: 75, // 75e percentile pour le prix
      quantityLow: 25, // 25e percentile pour les ventes
      quantityHigh: 75, // 75e percentile pour les ventes
    };
  }

  // Extraire les valeurs pour chaque métrique
  const prices = productsWithSales.map((p) => p.price);
  const quantities = productsWithSales.map((p) => p.quantitySold);

  // Trier les valeurs pour calculer les percentiles
  const sortedPrices = [...prices].sort((a, b) => a - b);
  const sortedQuantities = [...quantities].sort((a, b) => a - b);

  // Calculer les percentiles (25% et 75% selon les spécifications)
  const getPercentile = (sortedArray: number[], percentile: number): number => {
    const index = Math.floor((sortedArray.length * percentile) / 100);
    return sortedArray[index];
  };

  return {
    marginLow: 55, // Seuil fixe pour la marge faible (< 55%)
    marginHigh: 65, // Seuil fixe pour la marge élevée (> 65%)
    priceLow: getPercentile(sortedPrices, 25), // 25e percentile pour le prix
    priceHigh: getPercentile(sortedPrices, 75), // 75e percentile pour le prix
    quantityLow: getPercentile(sortedQuantities, 25), // 25e percentile pour les ventes
    quantityHigh: getPercentile(sortedQuantities, 75), // 75e percentile pour les ventes
  };
};

/**
 * Évalue un produit et détermine sa recommandation en fonction de ses métriques
 * @param product Produit à évaluer
 * @param allProducts Tous les produits du catalogue pour le contexte
 * @returns Résultat du scoring avec recommandation et scores
 */
export const evaluateProduct = (
  product: ProductWithSales,
  allProducts: ProductWithSales[]
): ProductScoringResult => {
  // Cas spécial: produit sans ventes et sans stock
  const isProductWithoutSalesAndStock = product.totalInventory === 0 && product.quantitySold === 0;

  // On va quand même calculer les seuils et les scores pour ces produits
  // mais on ne les inclut pas dans le calcul des percentiles

  // Calculer les seuils dynamiques basés sur le catalogue
  const thresholds = calculateThresholds(allProducts);

  // Extraire les valeurs min et max pour normaliser
  // Exclure les produits qui ont 0 en stock et 0 en ventes
  const productsWithSales = allProducts.filter(
    (p) => !(p.totalInventory === 0 && p.quantitySold === 0)
  );

  // Valeurs par défaut si aucun produit n'a de ventes
  if (productsWithSales.length === 0) {
    return {
      recommendation: ProductRecommendation.A_LIQUIDER,
      priorityLevel: PriorityLevel.PRIO_6,
      bgColor: "bg-red-500",
      rowClass: "hover:bg-red-50 bg-red-50/30",
      marginScore: 50,
      priceScore: 50,
      quantityScore: 50,
      totalScore: 50,
      marginLevel: "low",
      priceLevel: "low",
      quantityLevel: "low",
    };
  }

  // Extraire les valeurs min et max pour les prix et quantités uniquement
  // La marge est évaluée selon des seuils absolus
  const prices = productsWithSales.map((p) => p.price);
  const quantities = productsWithSales.map((p) => p.quantitySold);

  const maxPrice = Math.max(...prices);
  const minPrice = Math.min(...prices);
  const maxQuantity = Math.max(...quantities);
  const minQuantity = Math.min(...quantities);

  // Calculer les scores normalisés (0-100)
  // Pour la marge, on utilise directement la valeur absolue (pas de normalisation)
  const marginScore = product.margin; // Valeur absolue de la marge (en pourcentage)

  // Pour l'affichage et le score global, on utilise une valeur normalisée
  const normalizedPriceScore = normalizeValue(
    product.price,
    minPrice,
    maxPrice
  );

  const quantityScore =
    product.quantitySold > 0
      ? normalizeValue(product.quantitySold, minQuantity, maxQuantity)
      : 0; // Si pas de ventes, score de 0

  // Déterminer la catégorie du produit pour chaque dimension
  // Pour la marge, on utilise directement les seuils absolus spécifiés
  const isMarginLow = product.margin < 55; // Marge faible < 55%
  const isMarginMedium = product.margin >= 55 && product.margin <= 65; // Marge moyenne entre 55% et 65%
  const isMarginHigh = product.margin > 65; // Marge élevée > 65%

  // Utiliser la valeur brute du prix pour la comparaison avec les percentiles
  const isPriceLow = product.price < thresholds.priceLow;
  const isPriceMedium =
    product.price >= thresholds.priceLow &&
    product.price < thresholds.priceHigh;
  const isPriceHigh = product.price >= thresholds.priceHigh;

  const isQuantityLow = quantityScore < thresholds.quantityLow;
  const isQuantityMedium =
    quantityScore >= thresholds.quantityLow &&
    quantityScore < thresholds.quantityHigh;
  const isQuantityHigh = quantityScore >= thresholds.quantityHigh;

  // Calculer le score global (combinaison pondérée des trois dimensions)
  // Coefficients ajustables selon l'importance relative de chaque dimension
  const marginWeight = 0.4; // La marge est très importante
  const priceWeight = 0.3; // Le prix est moyennement important
  const quantityWeight = 0.3; // La quantité vendue est moyennement importante

  // Pour les produits sans ventes et sans stock, on utilise uniquement la marge et le prix
  // avec une pondération différente (60% marge, 40% prix)
  let totalScore;
  if (isProductWithoutSalesAndStock) {
    totalScore = (0.6 * marginScore) + (0.4 * normalizedPriceScore);
    // Le score de quantité reste à 0 pour ces produits
  } else {
    totalScore =
      marginWeight * marginScore +
      priceWeight * normalizedPriceScore +
      quantityWeight * quantityScore;
  }

  // Déterminer les niveaux pour chaque dimension
  // Pour la marge, on utilise directement les valeurs absolues selon les seuils spécifiés
  const marginLevel =
    product.margin > 65 ? "high" : product.margin >= 55 ? "medium" : "low";
  // Déterminer le niveau de prix en fonction des seuils percentiles
  const priceLevel = isPriceHigh ? "high" : isPriceMedium ? "medium" : "low";
  const quantityLevel = isQuantityHigh
    ? "high"
    : isQuantityMedium
    ? "medium"
    : "low";

  // Appliquer les règles de décision selon les spécifications
  let recommendation: ProductRecommendation;
  let priorityLevel: PriorityLevel;
  let bgColor: string;
  let rowClass: string;

  // Implémentation des règles selon les spécifications

  // Si marge élevée (>65%)
  if (isMarginHigh) {
    if (isPriceHigh && isQuantityHigh) {
      // Marge élevée, prix élevé, ventes élevées
      recommendation = ProductRecommendation.PERFORMANT;
      priorityLevel = PriorityLevel.PRIO_1;
      bgColor = "bg-blue-500";
      rowClass = "hover:bg-blue-50 bg-blue-50/30";
    } else if (isPriceMedium && isQuantityHigh) {
      // Marge élevée, prix moyen, ventes élevées
      recommendation = ProductRecommendation.AUGMENTER_PRIX;
      priorityLevel = PriorityLevel.PRIO_2;
      bgColor = "bg-green-500";
      rowClass = "hover:bg-green-50 bg-green-50/30";
    } else if (isPriceHigh && (isQuantityMedium || isQuantityLow)) {
      // Marge élevée, prix élevé, ventes moyennes ou faibles
      recommendation = ProductRecommendation.A_POUSSER;
      priorityLevel = PriorityLevel.PRIO_2;
      bgColor = "bg-green-500";
      rowClass = "hover:bg-green-50 bg-green-50/30";
    } else if (isPriceLow && isQuantityLow) {
      // Marge élevée, prix faible, ventes faibles
      recommendation = ProductRecommendation.A_ETUDIER;
      priorityLevel = PriorityLevel.PRIO_5;
      bgColor = "bg-yellow-500";
      rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
    } else if (isPriceMedium && isQuantityLow) {
      // Marge élevée, prix moyen, ventes faibles
      recommendation = ProductRecommendation.A_ETUDIER;
      priorityLevel = PriorityLevel.PRIO_4;
      bgColor = "bg-yellow-400";
      rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
    } else if (isPriceMedium && isQuantityMedium) {
      // Marge élevée, prix moyen, ventes moyennes
      recommendation = ProductRecommendation.PERFORMANT;
      priorityLevel = PriorityLevel.PRIO_3;
      bgColor = "bg-blue-400";
      rowClass = "hover:bg-blue-50 bg-blue-50/30";
    } else if (isPriceLow && isQuantityHigh) {
      // Marge élevée, prix faible, ventes élevées
      recommendation = ProductRecommendation.AUGMENTER_PRIX;
      priorityLevel = PriorityLevel.PRIO_3;
      bgColor = "bg-green-400";
      rowClass = "hover:bg-green-50 bg-green-50/30";
    }
  }
  // Si marge moyenne (55% < x < 65%)
  else if (isMarginMedium) {
    if (isPriceHigh && isQuantityHigh) {
      // Marge moyenne, prix élevé, ventes élevées
      recommendation = ProductRecommendation.PERFORMANT;
      priorityLevel = PriorityLevel.PRIO_1;
      bgColor = "bg-blue-500";
      rowClass = "hover:bg-blue-50 bg-blue-50/30";
    } else if (isPriceMedium && isQuantityHigh) {
      // Marge moyenne, prix moyen, ventes élevées
      recommendation = ProductRecommendation.AUGMENTER_PRIX;
      priorityLevel = PriorityLevel.PRIO_2;
      bgColor = "bg-green-500";
      rowClass = "hover:bg-green-50 bg-green-50/30";
    } else if (isPriceHigh && isQuantityMedium) {
      // Marge moyenne, prix élevé, ventes moyennes
      recommendation = ProductRecommendation.PERFORMANT;
      priorityLevel = PriorityLevel.PRIO_3;
      bgColor = "bg-blue-400";
      rowClass = "hover:bg-blue-50 bg-blue-50/30";
    } else if (isPriceMedium && isQuantityMedium) {
      // Marge moyenne, prix moyen, ventes moyennes
      recommendation = ProductRecommendation.PERFORMANT;
      priorityLevel = PriorityLevel.PRIO_4;
      bgColor = "bg-blue-300";
      rowClass = "hover:bg-blue-50 bg-blue-50/30";
    } else if (isPriceLow && isQuantityHigh) {
      // Marge moyenne, prix faible, ventes élevées
      recommendation = ProductRecommendation.AUGMENTER_PRIX;
      priorityLevel = PriorityLevel.PRIO_3;
      bgColor = "bg-green-400";
      rowClass = "hover:bg-green-50 bg-green-50/30";
    } else if (isPriceLow && isQuantityLow) {
      // Marge moyenne, prix faible, ventes faibles
      recommendation = ProductRecommendation.A_ETUDIER;
      priorityLevel = PriorityLevel.PRIO_5;
      bgColor = "bg-yellow-500";
      rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
    }
  }
  // Si marge faible (<55%)
  else if (isMarginLow) {
    if (isPriceHigh && isQuantityHigh) {
      // Marge faible, prix élevé, ventes élevées
      recommendation = ProductRecommendation.RENEGOCIER;
      priorityLevel = PriorityLevel.PRIO_2;
      bgColor = "bg-orange-500";
      rowClass = "hover:bg-orange-50 bg-orange-50/30";
    } else if (isPriceMedium && isQuantityHigh) {
      // Marge faible, prix moyen, ventes élevées
      recommendation = ProductRecommendation.AUGMENTER_PRIX;
      priorityLevel = PriorityLevel.PRIO_4;
      bgColor = "bg-orange-400";
      rowClass = "hover:bg-orange-50 bg-orange-50/30";
    } else if (isPriceHigh && isQuantityLow) {
      // Marge faible, prix élevé, ventes faibles
      recommendation = ProductRecommendation.A_ETUDIER;
      priorityLevel = PriorityLevel.PRIO_5;
      bgColor = "bg-yellow-500";
      rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
    } else if (isPriceHigh && isQuantityMedium) {
      // Marge faible, prix élevé, ventes moyennes
      recommendation = ProductRecommendation.RENEGOCIER;
      priorityLevel = PriorityLevel.PRIO_4;
      bgColor = "bg-orange-400";
      rowClass = "hover:bg-orange-50 bg-orange-50/30";
    } else if (isPriceMedium && isQuantityMedium) {
      // Marge faible, prix moyen, ventes moyennes
      recommendation = ProductRecommendation.RENEGOCIER;
      priorityLevel = PriorityLevel.PRIO_5;
      bgColor = "bg-orange-300";
      rowClass = "hover:bg-orange-50 bg-orange-50/30";
    } else {
      // Tous les autres cas de marge faible
      // (prix faible et ventes moyennes/faibles, prix moyen et ventes faibles, prix faible et ventes élevées)
      // Différencier entre 'à liquider' (avec stock) et 'à retirer' (sans stock)
      if (product.totalInventory > 0) {
        recommendation = ProductRecommendation.A_LIQUIDER;
      } else {
        recommendation = ProductRecommendation.A_RETIRER;
      }
      priorityLevel = PriorityLevel.PRIO_6;
      bgColor = "bg-red-500";
      rowClass = "hover:bg-red-50 bg-red-50/30";
    }
  }

  // Traitement spécifique pour les produits sans ventes et sans stock
  if (isProductWithoutSalesAndStock) {
    // On détermine la recommandation en fonction de la marge et du prix uniquement
    if (isMarginHigh) {
      if (isPriceHigh) {
        // Marge élevée, prix élevé - Produit potentiellement intéressant mais à étudier
        recommendation = ProductRecommendation.A_ETUDIER;
        priorityLevel = PriorityLevel.PRIO_5;
        bgColor = "bg-yellow-500";
        rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
      } else if (isPriceMedium) {
        // Marge élevée, prix moyen - Produit à étudier
        recommendation = ProductRecommendation.A_ETUDIER;
        priorityLevel = PriorityLevel.PRIO_5;
        bgColor = "bg-yellow-400";
        rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
      } else {
        // Marge élevée, prix bas - Produit à étudier
        recommendation = ProductRecommendation.A_ETUDIER;
        priorityLevel = PriorityLevel.PRIO_5;
        bgColor = "bg-yellow-400";
        rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
      }
    } else if (isMarginMedium) {
      // Marge moyenne - Produit à étudier avec priorité plus faible
      recommendation = ProductRecommendation.A_ETUDIER;
      priorityLevel = PriorityLevel.PRIO_5;
      bgColor = "bg-yellow-300";
      rowClass = "hover:bg-yellow-50 bg-yellow-50/30";
    } else {
      // Marge faible - Produit à retirer (car pas de stock actuel)
      recommendation = ProductRecommendation.A_RETIRER;
      priorityLevel = PriorityLevel.PRIO_6;
      bgColor = "bg-red-500";
      rowClass = "hover:bg-red-50 bg-red-50/30";
    }
  }
  
  // Si aucune règle n'a été appliquée (cas improbable), utiliser une valeur par défaut
  if (recommendation === undefined) {
    recommendation = ProductRecommendation.A_ETUDIER;
    priorityLevel = PriorityLevel.PRIO_5;
    bgColor = "bg-gray-300";
    rowClass = "hover:bg-gray-50";
  }

  return {
    recommendation,
    priorityLevel,
    bgColor,
    rowClass,
    marginScore,
    priceScore: normalizedPriceScore, // Utiliser le score normalisé pour l'affichage
    quantityScore,
    totalScore,
    marginLevel,
    priceLevel,
    quantityLevel,
  };
};

/**
 * Calcule le score de priorité pour le réapprovisionnement d'un produit
 * @param product Produit à évaluer
 * @param daysToForecast Nombre de jours pour lesquels on veut prévoir le stock
 * @param allProducts Tous les produits du catalogue pour le contexte
 * @returns Résultat du scoring de réapprovisionnement
 */
export const calculateReorderPriority = (
  product: ProductWithSales,
  daysToForecast: number,
  allProducts: ProductWithSales[],
  datePeriodDays: number = 30 // Valeur par défaut de 30 jours pour compatibilité
): ReorderScoringResult => {
  // Si le produit a 0 en stock et 0 en ventes, on le marque comme basse priorité
  if (product.totalInventory === 0 && product.quantitySold === 0) {
    return {
      priorityScore: 0,
      recommendedQuantity: 0,
      daysUntilStockout: 999,
      priorityLevel: "BASSE",
      priorityColor: "bg-gray-100",
    };
  }

  // Évaluer le produit pour obtenir son niveau de priorité stratégique
  const productScoring = evaluateProduct(product, allProducts);
  const { priorityLevel: strategicPriority } = productScoring;

  // Extraire les métriques clés du produit
  const { margin, quantitySold, totalInventory, daysRemaining } = product;

  // Calculer les ventes quotidiennes moyennes en utilisant la période réelle entre startDate et endDate
  // Note: Dans le tableau, on affiche avec .toFixed(1) mais pour les calculs
  // nous gardons la précision complète pour éviter des erreurs d'arrondi
  const dailySales = quantitySold / datePeriodDays;

  // Nombre de jours avant rupture de stock (utiliser daysRemaining s'il existe déjà)
  const daysUntilStockout =
    daysRemaining ||
    (dailySales > 0 ? Math.round(totalInventory / dailySales) : 999);

  // Calculer la quantité recommandée pour couvrir la période demandée
  // On ajoute un buffer de sécurité variable selon le niveau de priorité stratégique
  // Plus la priorité est élevée (P1, P2), plus le buffer est important
  let safetyFactor = 1.0;
  if (dailySales > 0) {
    switch (strategicPriority) {
      case PriorityLevel.PRIO_1: // Priorité maximale
        safetyFactor = 1.5; // Buffer de 50%
        break;
      case PriorityLevel.PRIO_2:
        safetyFactor = 1.4; // Buffer de 40%
        break;
      case PriorityLevel.PRIO_3:
        safetyFactor = 1.3; // Buffer de 30%
        break;
      case PriorityLevel.PRIO_4:
        safetyFactor = 1.2; // Buffer de 20%
        break;
      case PriorityLevel.PRIO_5:
        safetyFactor = 1.1; // Buffer de 10%
        break;
      case PriorityLevel.PRIO_6: // Priorité minimale
        safetyFactor = 1.0; // Pas de buffer supplémentaire
        break;
      default:
        safetyFactor = 1.2; // Buffer par défaut de 20%
    }
  }

  // Si le nombre de jours avant rupture de stock est supérieur à la période de prévision,
  // on ne recommande pas de réapprovisionnement (quantité = 0)
  // Sauf pour les produits de haute priorité (P1, P2) où on anticipe davantage
  const anticipationDays = strategicPriority <= PriorityLevel.PRIO_2 ? 15 : 0;
  const effectiveForecastDays = daysToForecast + anticipationDays;

  // Calcul de la quantité recommandée en tenant compte du stock existant et de la priorité stratégique
  let recommendedQuantity = 0;

  // Pour les produits de haute priorité (P1, P2, P3), on veut s'assurer qu'ils sont toujours disponibles
  // Pour les produits de priorité moyenne (P4), on veut éviter les ruptures de stock
  // Pour les produits de faible priorité (P5, P6), on commande uniquement si nécessaire

  // Calcul précis des unités nécessaires pour couvrir exactement la période de prévision
  const unitsNeededForPeriod = dailySales * effectiveForecastDays;

  // Calcul des unités manquantes (en tenant compte du stock existant)
  const missingUnits = Math.max(0, unitsNeededForPeriod - totalInventory);

  // Déterminer si nous devons commander en fonction de la priorité stratégique et du stock actuel
  let shouldOrder = false;

  if (dailySales > 0) {
    // Ne pas commander si aucune vente
    if (strategicPriority <= PriorityLevel.PRIO_3) {
      // Produits prioritaires (P1-P3): commander si le stock ne couvre pas la période + marge de sécurité
      shouldOrder = daysUntilStockout <= effectiveForecastDays * 1.2;
    } else if (strategicPriority === PriorityLevel.PRIO_4) {
      // Produits de priorité moyenne (P4): commander si le stock ne couvre pas exactement la période
      shouldOrder = daysUntilStockout <= effectiveForecastDays;
    } else {
      // Produits de faible priorité (P5-P6): commander uniquement si risque de rupture pendant la période
      shouldOrder = daysUntilStockout < effectiveForecastDays * 0.9;
    }
  }

  if (shouldOrder) {
    // Calcul de la quantité optimale pour que le stock soit épuisé exactement à la fin de la période
    // en tenant compte du facteur de sécurité et de la priorité stratégique

    // Appliquer le facteur de sécurité aux unités manquantes
    const baseQuantity = Math.ceil(missingUnits * safetyFactor);

    // Pour les produits prioritaires, s'assurer d'une quantité minimale de commande
    // basée sur les ventes quotidiennes pour éviter les petites commandes fréquentes
    let minOrderQuantity = 0;

    if (strategicPriority <= PriorityLevel.PRIO_2) {
      // Pour P1-P2: minimum de 30 jours de ventes
      minOrderQuantity = Math.ceil(dailySales * 30);
    } else if (strategicPriority === PriorityLevel.PRIO_3) {
      // Pour P3: minimum de 15 jours de ventes
      minOrderQuantity = Math.ceil(dailySales * 15);
    } else if (strategicPriority === PriorityLevel.PRIO_4) {
      // Pour P4: minimum de 7 jours de ventes
      minOrderQuantity = Math.ceil(dailySales * 7);
    }

    // Prendre le maximum entre la quantité calculée et la quantité minimale de commande
    recommendedQuantity = Math.max(baseQuantity, minOrderQuantity);

    // Arrondir à des lots logistiques pour faciliter la gestion des commandes
    // (par exemple, commander par multiples de 5 ou 10 selon le volume)
    if (recommendedQuantity > 50) {
      // Arrondir au multiple de 10 supérieur pour les grandes quantités
      recommendedQuantity = Math.ceil(recommendedQuantity / 10) * 10;
    } else if (recommendedQuantity > 10) {
      // Arrondir au multiple de 5 supérieur pour les quantités moyennes
      recommendedQuantity = Math.ceil(recommendedQuantity / 5) * 5;
    }
  }

  // Normaliser les valeurs pour le contexte du catalogue
  // Exclure les produits qui ont 0 en stock et 0 en ventes
  const productsWithSales = allProducts.filter(
    (p) =>
      p.quantitySold > 0 && !(p.totalInventory === 0 && p.quantitySold === 0)
  );

  if (productsWithSales.length === 0) {
    return {
      priorityScore: 0,
      recommendedQuantity: 0,
      daysUntilStockout: 999,
      priorityLevel: "BASSE",
      priorityColor: "bg-gray-300",
    };
  }

  // Extraire les valeurs pour normalisation
  const margins = productsWithSales.map((p) => p.margin);
  const salesRates = productsWithSales.map(
    (p) => p.quantitySold / datePeriodDays
  );

  const maxMargin = Math.max(...margins);
  const minMargin = Math.min(...margins);
  const maxSalesRate = Math.max(...salesRates);
  const minSalesRate = Math.min(...salesRates);

  // Normaliser les métriques (0-100)
  const marginScore = normalizeValue(margin, minMargin, maxMargin);
  const salesRateScore =
    dailySales > 0 ? normalizeValue(dailySales, minSalesRate, maxSalesRate) : 0;

  // Score pour les jours restants (inversé: moins de jours = score plus élevé)
  // Plafonné à 100 jours pour éviter les valeurs extrêmes
  const daysScore =
    daysUntilStockout < 100
      ? 100 - normalizeValue(daysUntilStockout, 0, 100)
      : 0;

  // Score basé sur le niveau de priorité stratégique (inversé: P1=100, P6=0)
  const priorityLevelScore = 100 - (strategicPriority - 1) * 20;

  // Pondération des facteurs
  const marginWeight = 0.2; // Importance de la marge
  const salesRateWeight = 0.3; // Importance du taux de vente
  const daysWeight = 0.3; // Importance des jours restants
  const priorityLevelWeight = 0.2; // Importance du niveau de priorité stratégique

  // Calculer le score final en intégrant le niveau de priorité stratégique
  let priorityScore =
    marginWeight * marginScore +
    salesRateWeight * salesRateScore +
    daysWeight * daysScore +
    priorityLevelWeight * priorityLevelScore;

  // Ajustement pour les produits de haute priorité stratégique (P1, P2)
  // On augmente leur score de priorité de réapprovisionnement
  if (strategicPriority <= PriorityLevel.PRIO_2) {
    priorityScore = Math.min(priorityScore * 1.2, 100); // Boost de 20%, plafonné à 100
  }

  // Ajustement: si pas de ventes, stock très élevé, ou pas besoin de réapprovisionnement, réduire la priorité
  // Mais on est moins sévère pour les produits de haute priorité stratégique
  if (dailySales === 0 || daysUntilStockout > 180) {
    const minScore = strategicPriority <= PriorityLevel.PRIO_3 ? 30 : 10;
    priorityScore = Math.min(priorityScore, minScore);
  }

  // Si le produit n'a pas besoin d'être réapprovisionné dans la période spécifiée,
  // on réduit le score mais on ne le met pas à 0 pour les produits de haute priorité
  if (daysUntilStockout > effectiveForecastDays) {
    if (strategicPriority <= PriorityLevel.PRIO_2) {
      priorityScore = Math.min(priorityScore, 25); // Maintenir un score minimal pour les produits prioritaires
    } else {
      priorityScore = 0;
    }
  }

  // Déterminer le niveau de priorité
  let priorityLevel: "CRITIQUE" | "HAUTE" | "MOYENNE" | "BASSE";
  let priorityColor: string;

  if (priorityScore >= 75) {
    priorityLevel = "CRITIQUE";
    priorityColor = "bg-red-500";
  } else if (priorityScore >= 50) {
    priorityLevel = "HAUTE";
    priorityColor = "bg-orange-500";
  } else if (priorityScore >= 25) {
    priorityLevel = "MOYENNE";
    priorityColor = "bg-yellow-500";
  } else {
    priorityLevel = "BASSE";
    priorityColor = "bg-gray-300";
  }

  return {
    priorityScore: Math.round(priorityScore),
    recommendedQuantity,
    daysUntilStockout,
    priorityLevel,
    priorityColor,
  };
};
